/**
 * Change the password of the account you are already signed in as.
 *
 * Unlike the reset flow, this proves ownership with the current password rather
 * than an emailed token, so it succeeds and stays put — there is no navigation
 * to make, and the confirmation is the whole feedback.
 */

import { MIN_PASSWORD_LENGTH } from "@bigbear/shared";
import { type FormEvent, useState } from "react";

import { AuthError, type AuthApi } from "../../auth/authApi";
import { Field, firstMissing } from "./AuthCard";

export function ChangePasswordForm({ api }: { api: AuthApi }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const missing = firstMissing([
      { label: "Current password", value: currentPassword },
      { label: "New password", value: newPassword },
    ]);
    if (missing) {
      setError(missing);
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword === currentPassword) {
      setError("That's the password you already have. Choose a different one.");
      return;
    }

    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setDone(true);
      // Nothing to re-submit, and leaving a password sitting in a form field
      // on a page someone may walk away from serves no purpose.
      setCurrentPassword("");
      setNewPassword("");
    } catch (cause) {
      setError(explain(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {done && (
        <p className="auth__notice" role="status">
          Password changed. Any other devices you were signed in on have been signed out.
        </p>
      )}
      {error && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      <Field
        id="currentPassword"
        label="Current password"
        type="password"
        value={currentPassword}
        autoComplete="current-password"
        onChange={setCurrentPassword}
      />
      <Field
        id="newPassword"
        label="New password"
        type="password"
        value={newPassword}
        autoComplete="new-password"
        onChange={setNewPassword}
        hint={`At least ${MIN_PASSWORD_LENGTH} characters. Other devices will be signed out.`}
      />

      <button type="submit" className="button button--primary auth__submit" disabled={busy}>
        {busy ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

function explain(cause: unknown): string {
  if (!(cause instanceof AuthError)) {
    return "Could not reach the server. Please try again.";
  }
  // The API says "Invalid password", which on a form with two password fields
  // does not say which one it means.
  if (cause.code === "INVALID_PASSWORD") {
    return "That isn't your current password.";
  }
  return cause.message;
}
